// src/services/mapbox/geocoding.service.js
import { MAPBOX_CONFIG } from '../components/map/mapbox.config';

// Centralized API config
const API_CONFIG = {
  BASE_URL: MAPBOX_CONFIG.API_ENDPOINTS?.GEOCODING,
  ACCESS_TOKEN: MAPBOX_CONFIG.ACCESS_TOKEN,
  DEFAULT_LIMIT: MAPBOX_CONFIG.DEFAULTS?.SEARCH_LIMIT || 5,
  DEFAULT_COUNTRY: MAPBOX_CONFIG.DEFAULTS?.COUNTRY || null,
  DEFAULT_LANGUAGE: MAPBOX_CONFIG.DEFAULTS?.LANGUAGE || 'en',
  TIMEOUT: 10000,
  RETRY_ATTEMPTS: 2,
};

if (!API_CONFIG.BASE_URL) {
  throw new Error('Mapbox GEOCODING URL missing in MAPBOX_CONFIG.API_ENDPOINTS.GEOCODING');
}

if (!API_CONFIG.ACCESS_TOKEN) {
  throw new Error('Mapbox access token missing. Set REACT_APP_MAPBOX_TOKEN in your .env');
}

// Request queue for rate limiting
let requestQueue = [];
let isProcessingQueue = false;

const processQueue = async () => {
  if (isProcessingQueue || requestQueue.length === 0) return;

  isProcessingQueue = true;

  while (requestQueue.length > 0) {
    const request = requestQueue.shift();
    await request();

    const delay = 60000 / MAPBOX_CONFIG.BILLING_OPTIMIZATIONS.maxRequestsPerMinute;
    await new Promise(resolve => setTimeout(resolve, delay));
  }

  isProcessingQueue = false;
};

// Simple in-memory cache
const cache = new Map();

// Clear cache function (optional export)
export const clearGeocodingCache = () => {
  cache.clear();
};

// Main geocoding service function
export const searchPlaces = async (query, options = {}) => {
  if (!query || query.trim().length < MAPBOX_CONFIG.BILLING_OPTIMIZATIONS.minCharsForSearch) {
    return [];
  }

  const {
    country = API_CONFIG.DEFAULT_COUNTRY,
    limit = Math.min(options.limit || API_CONFIG.DEFAULT_LIMIT, 10),
    language = API_CONFIG.DEFAULT_LANGUAGE,
    types = '',
    bbox = '',
    retryAttempts = API_CONFIG.RETRY_ATTEMPTS,
    useCache = true,
  } = options;

  const cacheKey = JSON.stringify({ query, country, limit, types, bbox, language });
  const cached = cache.get(cacheKey);
  const now = Date.now();

  if (useCache && cached && (now - cached.timestamp < MAPBOX_CONFIG.BILLING_OPTIMIZATIONS.cacheTTL)) {
    return cached.data;
  }

  const params = new URLSearchParams({
    access_token: API_CONFIG.ACCESS_TOKEN,
    limit,
    language,
    autocomplete: 'true',
  });

  if (country) params.append('country', country);
  if (types) params.append('types', types);
  if (bbox) params.append('bbox', bbox);

  const url = `${API_CONFIG.BASE_URL}/${encodeURIComponent(query)}.json?${params}`;

  return new Promise((resolve, reject) => {
    const executeRequest = async () => {
      let attempts = 0;
      while (attempts <= retryAttempts) {
        try {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

          const response = await fetch(url, { signal: controller.signal });
          clearTimeout(timeout);

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Mapbox API error (${response.status}): ${errorText}`);
          }

          const data = await response.json();

          if (useCache) {
            cache.set(cacheKey, { data: data.features || [], timestamp: Date.now() });
          }

          resolve(data.features || []);
          return;
        } catch (err) {
          attempts += 1;
          if (attempts > retryAttempts) {
            console.error('Geocoding request failed after retries:', err);
            reject(err);
            return;
          }
          // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempts)));
        }
      }
    };

    requestQueue.push(executeRequest);
    processQueue();
  });
};

// Additional utility functions you might want:
export const reverseGeocode = async (coordinates, options = {}) => {
  const [lng, lat] = coordinates;
  const params = new URLSearchParams({
    access_token: API_CONFIG.ACCESS_TOKEN,
    ...options,
  });

  const url = `${MAPBOX_CONFIG.API_ENDPOINTS?.REVERSE_GEOCODING || API_CONFIG.BASE_URL.replace('geocoding', 'reverse')}/${lng},${lat}.json?${params}`;

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Reverse geocoding failed: ${response.statusText}`);
    const data = await response.json();
    return data.features || [];
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return [];
  }
};

// Get current queue status
export const getQueueStatus = () => ({
  queueLength: requestQueue.length,
  isProcessing: isProcessingQueue,
  cacheSize: cache.size,
});